/**
 * ChannelPicker — multi-select de redes/canales para tareas.
 *
 * - Lee/escribe `redes_sociales` como JSON array string (compatible con CSV legacy)
 * - Catálogo built-in con emojis y colores
 * - Agregar canal custom (se guarda en localStorage para que persista entre sesiones)
 * - UI: badges actuales (clickeables) → popover con checkboxes + input "agregar"
 */

import { useState, useRef, useEffect, useLayoutEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import Glifo from "./Glifo.jsx";
import { Plus, X, Check } from "../lib/icons.jsx";
import { cn } from "../lib/utils.js";

// Catálogo built-in (canal → { emoji, label, badge, dot })
// `badge`: estilo del chip seleccionado (badge en celda) — bg con buen contraste en light + dark
// `dot`:   color del ícono/punto en filas del dropdown (siempre legible)
export const CANAL_CATALOGO = {
  instagram:    { emoji: "📷", label: "Instagram",    badge: "bg-[#EDE9FE] text-[#5B21B6] dark:bg-[#EDE9FE] dark:text-[#5B21B6]",       dot: "text-[#7C3AED] dark:text-[#A78BFA]" },
  facebook:     { emoji: "📘", label: "Facebook",     badge: "bg-[#CFFAFE] text-[#155E75] dark:bg-[#CFFAFE] dark:text-[#155E75]",       dot: "text-[#0E7490] dark:text-[#22D3EE]" },
  tiktok:       { emoji: "🎵", label: "TikTok",       badge: "bg-secondary text-foreground dark:bg-[#2B2E36]/60 dark:text-[#F2F3F6]",   dot: "text-foreground dark:text-[#CDD0D7]" },
  youtube:      { emoji: "▶",  label: "YouTube",      badge: "bg-[#FEE2E2] text-[#991B1B] dark:bg-[#FEE2E2] dark:text-[#991B1B]",            dot: "text-[#DC2626] dark:text-[#F87171]" },
  linkedin:     { emoji: "💼", label: "LinkedIn",     badge: "bg-[#CFFAFE] text-[#155E75] dark:bg-[#CFFAFE] dark:text-[#155E75]",            dot: "text-[#0E7490] dark:text-[#22D3EE]" },
  twitter:      { emoji: "𝕏",  label: "X / Twitter",  badge: "bg-secondary text-foreground dark:bg-[#2B2E36]/60 dark:text-[#F2F3F6]",       dot: "text-foreground dark:text-[#CDD0D7]" },
  threads:      { emoji: "🧵", label: "Threads",      badge: "bg-[#E4E6EB] text-[#1B1E25] dark:bg-[#2B2E36]/60 dark:text-[#F2F3F6]",   dot: "text-[#2B2E36] dark:text-[#CDD0D7]" },
  whatsapp:     { emoji: "💬", label: "WhatsApp",     badge: "bg-[#D1FAE5] text-[#065F46] dark:bg-[#D1FAE5] dark:text-[#065F46]", dot: "text-[#059669] dark:text-[#34D399]" },
  email:        { emoji: "✉",  label: "Email",        badge: "bg-[#FEF3C7] text-[#92400E] dark:bg-[#FEF3C7] dark:text-[#92400E]",   dot: "text-[#D97706] dark:text-[#FBBF24]" },
  interno:      { emoji: "🏠", label: "Interno",      badge: "bg-[#EDE9FE] text-[#5B21B6] dark:bg-[#EDE9FE] dark:text-[#5B21B6]", dot: "text-[#7C3AED] dark:text-[#A78BFA]" },
  "via-publica":{ emoji: "🛣", label: "Vía pública",  badge: "bg-[#FEF3C7] text-[#92400E] dark:bg-[#FEF3C7] dark:text-[#92400E]", dot: "text-[#D97706] dark:text-[#FBBF24]" },
  radio:        { emoji: "📻", label: "Radio",        badge: "bg-[#FEF3C7] text-[#92400E] dark:bg-[#FEF3C7] dark:text-[#92400E]", dot: "text-[#92400E] dark:text-[#92400E]" },
  tv:           { emoji: "📺", label: "TV",           badge: "bg-[#FEE2E2] text-[#991B1B] dark:bg-[#FEE2E2] dark:text-[#991B1B]",        dot: "text-[#DC2626] dark:text-[#F87171]" },
  prensa:       { emoji: "📰", label: "Prensa",       badge: "bg-secondary text-foreground dark:bg-[#2B2E36]/60 dark:text-[#F2F3F6]", dot: "text-foreground dark:text-[#CDD0D7]" },
  spotify:      { emoji: "🎧", label: "Spotify",      badge: "bg-[#D1FAE5] text-[#065F46] dark:bg-[#D1FAE5] dark:text-[#065F46]",     dot: "text-[#059669] dark:text-[#34D399]" },
  pinterest:    { emoji: "📌", label: "Pinterest",    badge: "bg-[#FEE2E2] text-[#7F1D1D] dark:bg-[#FEE2E2] dark:text-[#991B1B]",         dot: "text-[#B91C1C] dark:text-[#F87171]" },
};

// Aliases — entradas legacy se mapean al canónico al leer
const ALIAS = {
  ig: "instagram", fb: "facebook", tt: "tiktok", yt: "youtube",
  x: "twitter", wsp: "whatsapp", mail: "email", publica: "via-publica", vp: "via-publica",
};

const CUSTOM_KEY = "crew-canales-custom"; // localStorage

export function parseCanales(value) {
  if (!value) return [];
  let arr = [];
  if (Array.isArray(value)) arr = value;
  else if (typeof value === "string") {
    const trim = value.trim();
    if (trim.startsWith("[")) {
      try { arr = JSON.parse(trim); } catch { arr = trim.split(/[,;|]/); }
    } else {
      arr = trim.split(/[,;|]/);
    }
  }
  return arr
    .map(c => String(c).trim().toLowerCase())
    .filter(Boolean)
    .map(c => ALIAS[c] || c);
}

function loadCustom() {
  try { return JSON.parse(localStorage.getItem(CUSTOM_KEY)) || []; }
  catch { return []; }
}
function saveCustom(list) {
  try { localStorage.setItem(CUSTOM_KEY, JSON.stringify(list)); } catch {}
}

function styleFor(canal, customSet) {
  if (CANAL_CATALOGO[canal]) return CANAL_CATALOGO[canal];
  if (customSet.has(canal)) return {
    emoji: "🔖", label: canal,
    badge: "bg-[#CFFAFE] text-[#155E75] dark:bg-[#CFFAFE] dark:text-[#155E75]",
    dot:   "text-[#0E7490] dark:text-[#22D3EE]",
  };
  return {
    emoji: "📡", label: canal,
    badge: "bg-muted text-foreground dark:bg-[#2B2E36] dark:text-[#E4E6EB]",
    dot:   "text-muted-foreground",
  };
}

/**
 * Props:
 *   compact (default true) — limita a 3 badges en estado normal
 *   inline  (default false) — modo tabla: single-row, sin wrap, emoji-only,
 *                             altura fija. Garantiza filas uniformes.
 */
export default function ChannelPicker({ value, onChange, disabled = false, compact = true, inline = false }) {
  const [open, setOpen] = useState(false);
  const [customNew, setCustomNew] = useState("");
  const [customList, setCustomList] = useState(() => loadCustom());
  const popRef = useRef(null);
  const triggerRef = useRef(null);
  // El desplegable se dibuja fuera del contenedor (portal) y se ubica con las medidas de la pantalla: antes era
  // `absolute` dentro de la ventana con scroll, y en «Nueva tarea» (último campo) quedaba cortado: no abría ni
  // hacia arriba ni hacia abajo.
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    function medir() {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      const ancho = Math.min(288, window.innerWidth - 16);
      const abajo = window.innerHeight - r.bottom - 8;
      const arriba = r.top - 8;
      const hacerArriba = abajo < 320 && arriba > abajo;
      const disponible = Math.max(180, Math.min(420, hacerArriba ? arriba : abajo));
      const left = Math.max(8, Math.min(r.left, window.innerWidth - ancho - 8));
      setPos({ left, width: ancho, maxHeight: disponible, ...(hacerArriba ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) });
    }
    medir();
    window.addEventListener("resize", medir);
    window.addEventListener("scroll", medir, true);
    return () => { window.removeEventListener("resize", medir); window.removeEventListener("scroll", medir, true); };
  }, [open]);

  const selected = useMemo(() => new Set(parseCanales(value)), [value]);
  const customSet = useMemo(() => new Set(customList), [customList]);

  // Click outside cierra
  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (popRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      setOpen(false);
      setCustomNew("");
    }
    function escHandler(e) { if (e.key === "Escape") { setOpen(false); setCustomNew(""); } }
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", escHandler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", escHandler);
    };
  }, [open]);

  function toggle(canal) {
    const next = new Set(selected);
    if (next.has(canal)) next.delete(canal);
    else next.add(canal);
    onChange?.(JSON.stringify([...next]));
  }

  function addCustom() {
    const raw = customNew.trim().toLowerCase();
    if (!raw) return;
    if (CANAL_CATALOGO[raw] || customSet.has(raw)) {
      // Ya existe — solo lo seleccionamos
      const next = new Set(selected); next.add(raw);
      onChange?.(JSON.stringify([...next]));
      setCustomNew("");
      return;
    }
    const newList = [...customList, raw];
    setCustomList(newList);
    saveCustom(newList);
    const next = new Set(selected); next.add(raw);
    onChange?.(JSON.stringify([...next]));
    setCustomNew("");
  }

  function removeCustomFromCatalogo(canal) {
    const newList = customList.filter(c => c !== canal);
    setCustomList(newList);
    saveCustom(newList);
    if (selected.has(canal)) {
      const next = new Set(selected); next.delete(canal);
      onChange?.(JSON.stringify([...next]));
    }
  }

  const selectedArr = [...selected];
  const allOpciones = [
    ...Object.keys(CANAL_CATALOGO),
    ...customList.filter(c => !CANAL_CATALOGO[c]),
  ];

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        className={cn(
          "inline-flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors text-left",
          inline
            ? "h-6 w-full overflow-hidden"     // tabla → altura fija
            : "min-h-[24px] w-full",
          "hover:bg-white/5",
          disabled && "opacity-50 cursor-not-allowed"
        )}
      >
        {selectedArr.length === 0 ? (
          <span className="text-[12px] text-muted-foreground/40 italic">+ canal</span>
        ) : inline ? (
          // ── Modo tabla: emoji-only, sin wrap, máx 3 + "+N" ───────────────
          <div className="flex flex-nowrap items-center gap-0.5 overflow-hidden">
            {selectedArr.slice(0, 3).map((c, i) => {
              const s = styleFor(c, customSet);
              return (
                <span key={i} title={s.label}
                      className={cn(
                        "inline-flex items-center justify-center w-5 h-5 rounded-md text-[12px] flex-shrink-0",
                        s.badge
                      )}>
                  <Glifo g={s.emoji} className="text-[18px]" />
                </span>
              );
            })}
            {selectedArr.length > 3 && (
              <span className="text-[12px] text-muted-foreground/70 font-semibold ml-0.5 flex-shrink-0">
                +{selectedArr.length - 3}
              </span>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap gap-1">
            {selectedArr.slice(0, compact ? 3 : 99).map((c, i) => {
              const s = styleFor(c, customSet);
              return (
                <span key={i} title={s.label}
                      className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[12px] font-semibold", s.badge)}>
                  <Glifo g={s.emoji} className="text-[18px]" />
                  <span className="capitalize">{c.length > 10 ? c.slice(0, 8) + "…" : c}</span>
                </span>
              );
            })}
            {compact && selectedArr.length > 3 && (
              <span className="text-[12px] text-muted-foreground/60">+{selectedArr.length - 3}</span>
            )}
          </div>
        )}
      </button>

      {open && pos && createPortal(
        <div ref={popRef}
             style={{ position: "fixed", pointerEvents: "auto", left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight }}
             className="z-[300] flex flex-col bg-white dark:bg-[#1B1E25] border border-border dark:border-[#2B2E36] rounded-xl shadow-2xl py-0 overflow-hidden"
             onClick={e => e.stopPropagation()}>
          <div className="px-3 py-2 text-[12px] font-bold text-foreground dark:text-[#1B1E25] uppercase tracking-wider border-b border-border dark:border-[#E4E6EB] bg-muted dark:bg-[#F2F3F6] flex items-center justify-between">
            <span>Canales</span>
            <span className="text-primary normal-case tracking-normal">
              {selectedArr.length > 0 ? `${selectedArr.length} seleccionado${selectedArr.length > 1 ? "s" : ""}` : "ninguno"}
            </span>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">

          {allOpciones.map(canal => {
            const s = styleFor(canal, customSet);
            const isSelected = selected.has(canal);
            const isCustom = customSet.has(canal);
            return (
              <div key={canal}
                   className={cn(
                     "group flex items-center gap-2 px-2.5 py-2 cursor-pointer transition-colors",
                     isSelected
                       ? "bg-primary/10 hover:bg-primary/15"
                       : "hover:bg-muted dark:hover:bg-[#2B2E36]/60"
                   )}
                   onClick={() => toggle(canal)}>
                <div className={cn(
                  "w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors",
                  isSelected
                    ? "bg-primary border-primary"
                    : "border-[#A6ABB6] dark:border-[#6B6F7A]"
                )}>
                  {isSelected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                </div>
                <span className={cn("flex-shrink-0", s.dot)}><Glifo g={s.emoji} className="text-[20px]" /></span>
                <span className={cn(
                  "text-[12px] font-medium flex-1",
                  isSelected
                    ? "text-foreground dark:text-white"
                    : "text-foreground dark:text-[#E4E6EB]"
                )}>
                  {s.label}
                </span>
                {isCustom && (
                  <button
                    onClick={e => { e.stopPropagation(); removeCustomFromCatalogo(canal); }}
                    className="opacity-0 group-hover:opacity-100 text-[#DC2626] hover:bg-[#DC2626]/20 rounded p-0.5 transition-opacity"
                    title="Eliminar del catálogo">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })}

          </div>

          <div className="border-t border-border dark:border-[#E4E6EB] px-3 py-2.5 bg-muted dark:bg-[#F2F3F6]">
            <div className="flex gap-1.5">
              <input
                value={customNew}
                onChange={e => setCustomNew(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
                placeholder="Agregar canal nuevo…"
                className="flex-1 text-[12px] px-2.5 py-1.5 rounded-md border border-border dark:border-[#4B4F59] bg-white dark:bg-[#1B1E25] text-foreground dark:text-[#F2F3F6] placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary outline-none"
              />
              <button
                onClick={addCustom}
                disabled={!customNew.trim()}
                className="px-2.5 py-1.5 rounded-md bg-primary text-white text-[12px] font-semibold disabled:opacity-40 hover:opacity-90 flex items-center justify-center transition-opacity">
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
            <p className="text-[12px] text-muted-foreground dark:text-[#A6ABB6] mt-1.5">
              Tip · agregá canales propios y quedan guardados para próximas tareas.
            </p>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
